import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import LandingPage from './pages/LandingPage';
import SEO from './components/SEO';
import HelpChatWidget from './components/HelpChatWidget';
import { faqSchema } from './data/faqSchema';
import './App.css';

const QuoteFormPage = lazy(() => import('./pages/QuoteFormPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const CommercialServicesPage = lazy(() => import('./pages/CommercialServicesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminQuotesPage = lazy(() => import('./pages/AdminQuotesPage'));
const AdminQuoteDetailsPage = lazy(() => import('./pages/AdminQuoteDetailsPage'));
const AdminContractorRequestsPage = lazy(() => import('./pages/AdminContractorRequestsPage'));
const CustomerDashboardPage = lazy(() => import('./pages/CustomerDashboardPage'));
const ContractorDashboardPage = lazy(() => import('./pages/ContractorDashboardPage'));

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <Routes>
          <Route 
            path="/" 
            element={
              <>
                <SEO 
                  title="Commercial & Residential Cleaning Services Across Australia | TaskWombat"
                  description="Australia's most trusted and beneficial professional service provider, serving customers across Australia. Fully insured teams providing premium commercial cleaning contracts, residential cleaning, Airbnb cleaning, end of lease cleaning, and specialized cleaning services. Comprehensive $10M public liability coverage. Eco-friendly products. Bond-back guarantee. Satisfaction guaranteed. Exclusive seasonal offers. Join customers who prefer to engage with us. Get instant quotes today."
                  keywords="commercial cleaning Australia, contract cleaning, cleaning services Australia, professional cleaners, residential cleaning, end of lease cleaning, Airbnb cleaning, carpet cleaning, bond back cleaning, cleaning service near me, same day cleaning, eco-friendly cleaning, regular cleaning service, deep cleaning, steam cleaning, professional home cleaners, house cleaning service, domestic cleaning, bond cleaning, move out cleaning"
                  url="https://taskwombat.com/"
                  breadcrumbs={[{ name: 'Home', url: 'https://taskwombat.com/' }]}
                  structuredData={[faqSchema]}
                />
                <LandingPage />
              </>
            } 
          />
          <Route 
            path="/quote" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Get Instant Quote Tarneit, Hoppers Crossing, Point Cook, Williams Landing, Brighton, Malvern, Hawthorn - TaskWombat"
                  description="Get a free, transparent, instant quote for premium commercial cleaning contracts and residential cleaning services across Australia. No surprises, no hidden fees. Receive your personalized quote for commercial contracts, regular cleaning, end of lease cleaning, Airbnb cleaning, deep cleaning, and more. Same-day service available."
                  keywords="cleaning quote Tarneit, cleaning quote Hoppers Crossing, cleaning quote Point Cook, cleaning quote Williams Landing, instant quote, free cleaning quote, home cleaning price, cleaning service quote, get quote, cleaning cost estimate, bond cleaning quote, Airbnb cleaning quote, professional cleaning prices"
                  url="https://taskwombat.com/quote"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Get Quote', url: 'https://taskwombat.com/quote' }
                  ]}
                />
                <QuoteFormPage />
              </Suspense>
            } 
          />
          <Route 
            path="/thank-you" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Thank You - TaskWombat Quote Request Received"
                  description="Thank you for requesting a quote from TaskWombat. We'll send you a personalized quote within 24 hours. Our team will review your cleaning requirements and provide you with the best quote for your needs."
                  keywords="thank you, quote submitted, quote received, TaskWombat quote"
                  url="https://taskwombat.com/thank-you"
                  noindex={true}
                />
                <ThankYouPage />
              </Suspense>
            } 
          />
          <Route 
            path="/careers" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Careers - Join TaskWombat Cleaning Services Team Australia"
                  description="Join the TaskWombat team. We're always looking for talented, professional cleaners and support staff across Australia. Competitive rates, flexible hours, training provided. Join Australia's fastest-growing cleaning service."
                  keywords="cleaning jobs, cleaner jobs Australia, cleaning careers, join TaskWombat, cleaning service jobs, professional cleaner positions, cleaning contractor jobs"
                  url="https://taskwombat.com/careers"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Careers', url: 'https://taskwombat.com/careers' }
                  ]}
                />
                <CareersPage />
              </Suspense>
            } 
          />
          <Route 
            path="/features" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Features - Why Choose TaskWombat Cleaning Services Australia"
                  description="Discover why TaskWombat is Australia's most trusted and beneficial service provider. Verified professionals, fully insured (up to $10M public liability), eco-friendly products, flexible scheduling, same-day service, 24/7 support, satisfaction guarantee, and exclusive seasonal offers. Serving customers across Australia. Join thousands of satisfied customers who prefer to engage with us for their commercial cleaning contracts and residential cleaning needs."
                  keywords="cleaning service features, why choose TaskWombat, professional cleaners, insured cleaners, eco-friendly cleaning, verified cleaners, satisfaction guarantee, flexible scheduling, same day cleaning"
                  url="https://taskwombat.com/features"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Features', url: 'https://taskwombat.com/features' }
                  ]}
                />
                <FeaturesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/how-it-works" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="How It Works - Commercial Cleaning Contracts & Services | TaskWombat Australia"
                  description="Learn how TaskWombat works for commercial cleaning contracts and residential cleaning services. Simple process: Request a quote, site inspection for commercial contracts, professional service delivery, and quality assurance. Trusted by property managers and facility managers across Australia."
                  keywords="how commercial cleaning contracts work, commercial cleaning process, contract cleaning steps, property manager cleaning services, facility management cleaning"
                  url="https://taskwombat.com/how-it-works"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'How It Works', url: 'https://taskwombat.com/how-it-works' }
                  ]}
                />
                <HowItWorksPage />
              </Suspense>
            } 
          />
          <Route 
            path="/services" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Most Trusted Commercial & Residential Cleaning Services Across Australia - TaskWombat"
                  description="Australia's most trusted and beneficial services, serving customers across Australia. Professional commercial cleaning contracts, regular home cleaning, end of lease cleaning (bond-back guaranteed), Airbnb cleaning, deep cleaning, carpet cleaning, window cleaning, oven cleaning, outdoor cleaning, and more. Exclusive seasonal offers. Join customers who prefer to engage with us."
                  keywords="removalist services Tarneit, moving services Hoppers Crossing, cleaning services Point Cook, lawn mowing Williams Landing, cleaning services Brighton, cleaning services Malvern, cleaning services Hawthorn, removalist Brighton, removalist Malvern, removalist Hawthorn, lawn mowing Brighton, lawn mowing Malvern, lawn mowing Hawthorn, home cleaning services, regular cleaning, end of lease cleaning, bond cleaning, Airbnb cleaning, deep cleaning, carpet cleaning, window cleaning, oven cleaning, outdoor cleaning, commercial cleaning, steam cleaning, move in cleaning, move out cleaning, spring cleaning, Werribee cleaning, Truganina cleaning, removalist, moving company, lawn care, grass cutting"
                  url="https://taskwombat.com/services"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Services', url: 'https://taskwombat.com/services' }
                  ]}
                />
                <ServicesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/services/:serviceId" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <ServiceDetailPage />
              </Suspense>
            } 
          />
          <Route 
            path="/commercial-services" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Commercial & Contract Cleaning Services Australia - TaskWombat"
                  description="Professional commercial and contract cleaning services across Australia. Fully insured cleaning for offices, strata, facilities, and hospitality venues. Long-term contracts available. $10M public liability insurance. Police checked staff. OH&S compliant."
                  keywords="commercial cleaning Australia, contract cleaning, office cleaning, strata cleaning, facility cleaning, hospitality cleaning, commercial cleaning services"
                  url="https://taskwombat.com/commercial-services"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Commercial Services', url: 'https://taskwombat.com/commercial-services' }
                  ]}
                />
                <CommercialServicesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/contact" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Contact Us - TaskWombat Cleaning Services Australia | 24/7 Support"
                  description="Get in touch with TaskWombat, Australia's most trusted and beneficial service provider. Contact us via email, phone, or form. We're here to help with all your commercial cleaning contracts and residential cleaning needs across Australia. 24/7 customer support available. Exclusive seasonal offers for engaged customers."
                  keywords="contact TaskWombat, customer support, cleaning service contact, TaskWombat support, cleaning service phone number, cleaning service email, get in touch, Tarneit cleaning, Hoppers Crossing cleaning"
                  url="https://taskwombat.com/contact"
                  breadcrumbs={[
                    { name: 'Home', url: 'https://taskwombat.com/' },
                    { name: 'Contact', url: 'https://taskwombat.com/contact' }
                  ]}
                />
                <ContactPage />
              </Suspense>
            } 
          />
          <Route 
            path="/blog" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <BlogPage />
              </Suspense>
            } 
          />
          <Route 
            path="/blog/:slug" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <BlogPostPage />
              </Suspense>
            } 
          />
          <Route 
            path="/login" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <SEO 
                  title="Login - TaskWombat Account"
                  description="Login to your TaskWombat account to manage your cleaning services and bookings."
                  keywords="login, TaskWombat login, account login"
                  url="https://taskwombat.com/login"
                  noindex={true}
                />
                <LoginPage />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/dashboard" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <AdminDashboardPage />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/quotes" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <AdminQuotesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/quotes/:id" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <AdminQuoteDetailsPage />
              </Suspense>
            } 
          />
          <Route 
            path="/admin/contractor-requests" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <AdminContractorRequestsPage />
              </Suspense>
            } 
          />
          <Route 
            path="/customer/dashboard" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <CustomerDashboardPage />
              </Suspense>
            } 
          />
          <Route 
            path="/contractor/dashboard" 
            element={
              <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Loading...</div>}>
                <ContractorDashboardPage />
              </Suspense>
            } 
          />
        </Routes>
        <HelpChatWidget />
      </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
